/**
 * Turn a merchant-picked image file into a small square data URL suitable
 * for storing inline in Store.settings (widget agent avatar / launcher).
 * `fit: 'cover'` crops to fill the square (photos/avatars); `fit: 'contain'`
 * letterboxes the whole image onto a transparent canvas (logos/icons).
 * Prefers PNG, falling back to JPEG if the PNG encoding lands over the
 * server's 80k-char cap.
 */

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_DATA_URL_CHARS = 80_000;

export async function fileToAvatarDataUrl(
  file: File,
  size = 96,
  fit: 'cover' | 'contain' = 'cover',
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file');
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error('Image must be under 5 MB');
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not read that image'));
      el.src = objectUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not process the image');

    // cover: scale the shorter side to `size` and crop the overflow.
    // contain: fit the longer side inside `size` (never upscale) and center
    // on the transparent canvas so the whole image survives.
    const scale =
      fit === 'cover'
        ? size / Math.min(img.naturalWidth, img.naturalHeight)
        : Math.min(size / Math.max(img.naturalWidth, img.naturalHeight), 1);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);

    const png = canvas.toDataURL('image/png');
    if (png.length <= MAX_DATA_URL_CHARS) return png;
    const jpeg = canvas.toDataURL('image/jpeg', 0.85);
    if (jpeg.length <= MAX_DATA_URL_CHARS) return jpeg;
    throw new Error('Image is too complex to store — try a simpler one');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
